import React, {useMemo} from 'react';
import { useSelector } from 'react-redux';
import {
    getOrLoadUserDetails,
    getOrLoadUserPicture,
    getUserDefaultGlyphIcon,
} from 'modules/contacts/stores/contacts-slice-handler';
import { UserAvailability } from '../../../constants/contacts-constants';
import { getCompanySettings } from '../../settings/stores/company-settings-slice-handler';

/**
 * @param {string} msisdn The msisdn of the user.
 * @return {JSX.Element}
 * @constructor
 */
export default function ContactUserPicture({ msisdn, ...otherProps }) {
    const user = useSelector(() => getOrLoadUserDetails(msisdn));
    const userPicture = useSelector(() => getOrLoadUserPicture(msisdn));
    const initials = user === null ? msisdn.substring(0, 2) : user.ComputedUserInitials;
    const companySettings = getCompanySettings();

    let availabilityBubbleClasses = ['user-availability'];
    const isUserDisconnected = useMemo(() => {
        if (!companySettings.DisplaySubscriberAsDisconnectedWhenNotReachable && user?.technicalDisconnected) {
            return false;
        }
        return !user?.UserConnectedStatus;
    }, [companySettings, user]);
    
    if (user === null || isUserDisconnected) {
        availabilityBubbleClasses.push('not-connected');
    } else if (user.UserAvailability === UserAvailability.AVAILABLE) {
        availabilityBubbleClasses.push('available');
    } else {
        availabilityBubbleClasses.push('busy');
    }

    const renderPic = () => {
        if (userPicture !== null) {
            return (
                <div
                    className={'user-picture'}
                    data-test-id={`user-picture-${msisdn}`}
                    style={{ backgroundImage: `url("${userPicture}")`, backgroundSize: 'cover' }}
                ></div>
            );
        }

        const glyphIcon = getUserDefaultGlyphIcon(user);

        if (glyphIcon !== null) {
            return (
                <div className={'user-picture icon-picture'} data-test-id={`user-picture-${msisdn}`}>
                    <span className={`user-glyph-icon ${glyphIcon}`} />
                </div>
            );
        }

        return (
            <div className={'user-initials'} data-test-id={`user-initials-${msisdn}`}>
                {initials}
            </div>
        );
    };

    return (
        <div className="user-picture-frame" {...otherProps}>
            {companySettings.AllowUserAvailability && <div className={availabilityBubbleClasses.join(' ')}></div>}
            {renderPic()}
        </div>
    );
}
